import { FC, useEffect, useRef, useMemo, useState } from 'react';
import * as d3 from 'd3';
import dayjs from 'dayjs';

import { DriverData } from '../../../types/common';

import './index.css';

interface SpeedLineProps {
  speed: Record<string, number>;
  mileage: Record<string, number>;
  driverData?: DriverData | DriverData[];
  isToday: boolean;
}

interface FocusedDriver {
  name: string;
  color: string;
  driverId: string;
}

const DEFAULT_MAX_SPEED = 120;
const SVG_SIZE = 404.3;
const CENTER = SVG_SIZE / 2;
const TOTAL_COLUMNS = 54;
const VALID_COLUMNS = 48;
const TOTAL_ANGLE = 2 * Math.PI * (VALID_COLUMNS / TOTAL_COLUMNS);
const ROTATE_OFFSET = Math.PI + Math.PI / 2;
const START_ANGLE = -TOTAL_ANGLE / 2 + ROTATE_OFFSET;
const END_ANGLE = TOTAL_ANGLE / 2 + ROTATE_OFFSET;
const baseSize = 550
const ratio = 0.92;
const DEFAULT_DASHARRAY = SVG_SIZE * 0.6;

const fillData = (data: Record<string, number>, isToday: boolean): Record<string, number> => {
  const sortedKeys = Object.keys(data).sort();
  if (sortedKeys.length === 0) return {};
  const firstDate = dayjs(sortedKeys[0]);
  const date = firstDate.format('YYYY-MM-DD');
  const now = dayjs().format('HH:mm:ss');
  const start = firstDate.startOf('day');
  const end = firstDate.endOf('day');
  const currentTime = dayjs(`${date} ${now}`);

  const timeMap = new Map<string, number>();
  Object.entries(data).forEach(([time, value]) => timeMap.set(time, value));

  const filledData: Record<string, number> = {};
  for (let t = start.valueOf(); t <= end.valueOf(); t += 1000) {
    const key = dayjs(t).format('YYYY-MM-DD HH:mm:ss');
    filledData[key] = isToday && dayjs(t).isAfter(currentTime) ? 0 : timeMap.get(key) ?? 0;
  }
  return filledData;
};

const SpeedLineD3: FC<SpeedLineProps> = ({ speed, mileage, driverData, isToday = false }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [focusedDriver, setFocusedDriver] = useState<FocusedDriver | null>(null);

  const maxSpeed = useMemo(() => Math.max(...Object.values(speed), DEFAULT_MAX_SPEED), [speed]);
  const speedList = useMemo(() => {
    const speedMax = maxSpeed > DEFAULT_MAX_SPEED ? maxSpeed : DEFAULT_MAX_SPEED;
    return [
      { speed: 0, color: '#000', size: baseSize * Math.pow(ratio, 6) },
      { speed: 20, color: '#00FF00', size: baseSize * Math.pow(ratio, 5) },
      { speed: 40, color: '#66FF00', size: baseSize * Math.pow(ratio, 4) },
      { speed: 60, color: '#CCFF00', size: baseSize * Math.pow(ratio, 3) },
      { speed: 80, color: '#FFFF00', size: baseSize * Math.pow(ratio, 2) },
      { speed: 100, color: '#FF8000', size: baseSize * ratio },
      { speed: speedMax, color: '#FF0000', size: baseSize },
    ].sort((a, b) => a.speed - b.speed);
  }, [maxSpeed]);

  useEffect(() => {
    if (driverData) {
      if (Array.isArray(driverData)) {
        setFocusedDriver({
          name: driverData[0].name,
          color: driverData[0].color,
          driverId: driverData[0].cardId,
        })
      } else {
        setFocusedDriver({
          name: driverData.name,
          color: driverData.color,
          driverId: driverData.cardId,
        })
      }
    }
  }, [driverData])

  const mileageLevels = useMemo(() => {
    const maxMileage = Math.ceil(Math.max(...Object.values(mileage)));
    const mileageLayers = 5;
    const mileageLevels = [];
    for (let index = 0; index < mileageLayers; index++) {
      const mileageLevel = {
        mileage: maxMileage * ((mileageLayers - 1 - index) / (mileageLayers - 1)),
        size: !index ? DEFAULT_DASHARRAY : DEFAULT_DASHARRAY * Math.pow(0.9, index)
      };
      mileageLevels.push(mileageLevel);
    }
    return mileageLevels;
  }, [mileage]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // 清理舊的 tooltip
    if (tooltipRef.current) {
      tooltipRef.current.remove();
    }
    
    // 創建新的 tooltip 容器
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "speed-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border", "1px solid #ddd")
      .style("border-radius", "4px")
      .style("padding", "8px")
      .style("font-size", "12px")
      .style("box-shadow", "0 2px 4px rgba(0,0,0,0.2)")
      .style("pointer-events", "none")
      .style("z-index", "1001");

    tooltipRef.current = tooltip.node() as HTMLDivElement;

    // 繪製速度圓弧
    svg.selectAll("path.bg")
      .data(speedList)
      .enter()
      .append("path")
      .attr("class", "bg")
      .attr("d", d => {
        const r = d.size / 2;
        const x1 = CENTER + r * Math.cos(START_ANGLE);
        const y1 = CENTER + r * Math.sin(START_ANGLE);
        const x2 = CENTER + r * Math.cos(END_ANGLE);
        const y2 = CENTER + r * Math.sin(END_ANGLE);
        const largeArcFlag = END_ANGLE - START_ANGLE > Math.PI ? 1 : 0;
        return [
          `M ${x1} ${y1}`,
          `A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2}`
        ].join(' ');
      })
      .attr("stroke", d => d.color)
      .attr("fill", "none")
      .attr("stroke-width", 1);

    // 繪製里程虛線
    svg.selectAll("path.speed-line")
      .data(mileageLevels)
      .enter()
      .append("path")
      .attr("class", "speed-line")
      .attr("d", d => {
        const r = d.size / 2;
        const x1 = CENTER + r * Math.cos(START_ANGLE);
        const y1 = CENTER + r * Math.sin(START_ANGLE);
        const x2 = CENTER + r * Math.cos(END_ANGLE);
        const y2 = CENTER + r * Math.sin(END_ANGLE);
        const largeArcFlag = END_ANGLE - START_ANGLE > Math.PI ? 1 : 0;
        return [
          `M ${x1} ${y1}`,
          `A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2}`
        ].join(' ');
      })
      .attr("stroke", '#000')
      .attr("fill", "none")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "5,5");

    // 處理速度數據
    const filledSpeed = fillData(speed, isToday);
    const sortedSpeedEntries = Object.entries(filledSpeed).sort(([a], [b]) => a.localeCompare(b));
    const speedEntriesLength = sortedSpeedEntries.length;

    const speedCenterIndex = Math.floor(speedEntriesLength / 2);
    const speedCenterAngle = (speedCenterIndex / (speedEntriesLength - 1)) * TOTAL_ANGLE;
    const speedRotateOffset = -speedCenterAngle - Math.PI / 2;

    const speedPoints = sortedSpeedEntries.map(([_, value], i) => {
      const angle = (i / (speedEntriesLength - 1)) * TOTAL_ANGLE + speedRotateOffset;
      const r = d3.scaleLinear<number, number>()
        .domain([0, Math.max(...speedList.map(s => s.speed))])
        .range([speedList[0].size / 2, speedList[speedList.length - 1].size / 2])
        .clamp(true)(value);
      return [Math.cos(angle) * r + CENTER, Math.sin(angle) * r + CENTER] as [number, number];
    });

    // 處理里程數據
    const filledMileage = fillData(mileage, isToday);
    const sortedMileageEntries = Object.entries(filledMileage).sort(([a], [b]) => a.localeCompare(b));
    const mileageEntriesLength = sortedMileageEntries.length;

    const mileageCenterIndex = Math.floor(mileageEntriesLength / 2);
    const mileageCenterAngle = (mileageCenterIndex / (mileageEntriesLength - 1)) * TOTAL_ANGLE;
    const mileageRotateOffset = -mileageCenterAngle - Math.PI / 2;

    const mileagePoints = sortedMileageEntries.map(([_, value], i) => {
      const angle = (i / (mileageEntriesLength - 1)) * TOTAL_ANGLE + mileageRotateOffset;
      const r = d3.scaleLinear<number, number>()
        .domain([0, Math.max(...mileageLevels.map(s => s.mileage))])
        .range([mileageLevels[mileageLevels.length - 1].size / 2, mileageLevels[0].size / 2])
        .clamp(true)(value);
      return [Math.cos(angle) * r + CENTER, Math.sin(angle) * r + CENTER] as [number, number];
    });

    // 繪製速度線
    const speedLine = d3.line<[number, number]>()
      .x(d => d[0])
      .y(d => d[1]);

    svg.append("path")
      .datum(speedPoints)
      .attr("d", speedLine(speedPoints)!)
      .attr("stroke", "#000")
      .attr("fill", "none")
      .attr("stroke-width", 1);

    // 繪製里程線
    const mileageLine = d3.line<[number, number]>()
      .x(d => d[0])
      .y(d => d[1]);

    svg.append("path")
      .datum(mileagePoints)
      .attr("d", mileageLine(mileagePoints)!)
      .attr("stroke", "#000")
      .attr("fill", "none")
      .attr("stroke-width", 1);

    // 創建 hover 圓點
    const hoverCircle = svg.append("circle")
      .attr("r", 3)
      .attr("fill", "yellow")
      .attr("stroke", "black")
      .style("display", "none");

    // 添加 mousemove 事件
    svg.on("mousemove", function (event) {
      const [mx, my] = d3.pointer(event);
      let minDist = Infinity;
      let closestIndex = -1;
      let isSpeed = true;

      // 檢查速度點
      speedPoints.forEach(([x, y], i) => {
        const dist = (x - mx) ** 2 + (y - my) ** 2;
        if (dist < minDist) {
          minDist = dist;
          closestIndex = i;
          isSpeed = true;
        }
      });

      // 檢查里程點
      mileagePoints.forEach(([x, y], i) => {
        const dist = (x - mx) ** 2 + (y - my) ** 2;
        if (dist < minDist) {
          minDist = dist;
          closestIndex = i;
          isSpeed = false;
        }
      });

      const HOVER_THRESHOLD = 8;

      if (closestIndex >= 0 && Math.sqrt(minDist) < HOVER_THRESHOLD) {
        const points = isSpeed ? speedPoints : mileagePoints;
        const entries = isSpeed ? sortedSpeedEntries : sortedMileageEntries;
        const [x, y] = points[closestIndex];
        const [time, value] = entries[closestIndex];

        hoverCircle
          .attr("cx", x)
          .attr("cy", y)
          .style("display", "block");

        tooltip
          .style("visibility", "visible")
          .html(`
            <div>時間：${time}</div>
            <div>${isSpeed ? '速度' : '里程'}：${value}</div>
          `)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 10) + "px");
      } else {
        hoverCircle.style("display", "none");
        tooltip.style("visibility", "hidden");
      }
    });

    svg.on("mouseleave", () => {
      hoverCircle.style("display", "none");
      tooltip.style("visibility", "hidden");
    });

    if (driverData) {
      const WORK_TIME_ARC_SVG_SIZE = 300.15;
      const workArcOuter = WORK_TIME_ARC_SVG_SIZE / 2;
      const workArcInner = workArcOuter * 0.95;

      const drawWorkTimeArc = (start: string, end: string, color: string, driverName: string, driverId: string) => {
        const workStart = dayjs(start);
        let workEnd = dayjs(end);
        const date = workStart.format('YYYY-MM-DD');
        const now = dayjs().format('HH:mm:ss');
        const currentTime = dayjs(`${date} ${now}`);

        if (isToday && workStart.isAfter(currentTime)) {
          return;
        }

        if (isToday && workEnd.isAfter(currentTime)) {
          workEnd = currentTime;
        }

        const totalSecondsInDay = 24 * 60 * 60;
        const startSeconds = workStart.hour() * 3600 + workStart.minute() * 60 + workStart.second();
        const endSeconds = workEnd.hour() * 3600 + workEnd.minute() * 60 + workEnd.second();
        
        const startRatio = startSeconds / totalSecondsInDay;
        const endRatio = endSeconds / totalSecondsInDay;
      
        const angleOffset = 2 * Math.PI * (14 / TOTAL_COLUMNS);
        const workStartAngle = START_ANGLE + startRatio * TOTAL_ANGLE + angleOffset;
        const workEndAngle = START_ANGLE + endRatio * TOTAL_ANGLE + angleOffset;
      
        svg.append('path')
          .attr('d', d3.arc()({
            innerRadius: workArcInner,
            outerRadius: workArcOuter,
            startAngle: workStartAngle,
            endAngle: workEndAngle
          }))
          .attr('transform', `translate(${CENTER}, ${CENTER})`)
          .attr('fill', color)
          .attr('stroke', color)
          .attr('stroke-width', 2)
          .style('cursor', 'pointer')
          .on('mouseover', function() {
            d3.select(this)
              .attr('fill', d3.color(color)?.brighter(0.2)?.toString() ?? color)
              .attr('stroke', d3.color(color)?.brighter(0.2)?.toString() ?? color);
            
            setFocusedDriver({ name: driverName, color, driverId });
          })
          .on('mouseout', function() {
            d3.select(this)
              .attr('fill', color)
              .attr('stroke', color);
            
            setFocusedDriver({
              name: driverName,
              driverId,
              color,
            });
            tooltip.style('visibility', 'hidden');
          });
      };

      const processDriverData = (data: DriverData) => {
        if (data.driveHours) {
          const isMultipleDriveHours = Array.isArray(data.driveHours);
          if (isMultipleDriveHours) {
            // 處理多個工作時段
            (data.driveHours as Array<{ start: string; end: string }>).forEach(({ start, end }) => {
              drawWorkTimeArc(start, end, data.color, data.name, data.cardId);
            });
          } else {
            // 處理單一工作時段
            const { start, end } = data.driveHours as { start: string; end: string };
            drawWorkTimeArc(start, end, data.color, data.name, data.cardId);
          }
        }
      };

      // 處理 driverData 可能是陣列或單一物件的情況
      if (Array.isArray(driverData)) {
        driverData.forEach(processDriverData);
      } else {
        processDriverData(driverData);
      }
    }
  
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
      }
    };
  }, [speed, speedList, mileage, mileageLevels, driverData, isToday]);

  return (
    <>
      <svg
        ref={svgRef}
        width={SVG_SIZE}
        height={SVG_SIZE}
        className="speed-line-svg"
      />
      <div className="speed-line-container">
        <div className="speed-line-container-item">
          <p className="speed-line-container-item-speed">km/hr</p>
        </div>
        {speedList.map((item, index) => (
          item.speed ? <div key={index} className="speed-line-container-item" style={{ backgroundColor: item.color }}>
            <p className="speed-line-container-item-speed">{item.speed}</p>
          </div> : null
        ))}
      </div>
      {
        focusedDriver && (
          <div className="speed-line-driver-info">
            <p className="speed-line-driver-id">{focusedDriver.driverId}</p>
            <div className="speed-line-driver-info-item">
              <i className="speed-line-driver-info-item-icon" style={{ backgroundColor: focusedDriver.color }} />
              <p className="speed-line-driver-info-item-name">{focusedDriver.name}</p>
            </div>
          </div>
        )
      }
    </>
  );
};

export default SpeedLineD3;
